import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactDOM from "react-dom";

import { comma, uncomma, useFormInput } from "@/utils";
import { Confirm, InputPopup, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { ImageUpload } from "@/components/Common/ImageUpload";
import {
    AttachedFile,
    BasicInfo,
    BillingInfo,
    BusinessInfo,
    ContractDetailInfo,
    ContractInfo,
    ContractMemo,
    DepositHistory,
    OtherEtcInfo,
    OtherInfo,
    TotalBillingInfo,
} from "./components";
import {
    AgentPartnersPopup,
    BillingSettingPopup,
    InsuranceConfigPopup,
    OrderListPopup,
    SalesPartnersPopup,
    TermListPopup,
    VehicleListPopup,
    VehicleReturnPopup,
} from "./popups";

import { useLazyGetCarByIdQuery, useLazySearchCarByNumberQuery } from "@/redux/carApi";
import { useGetOrderDetailMutation } from "@/redux/orderApi";
import { usePostChargeMutation, usePostPreChargeMutation } from "@/redux/chargeApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import {
    useDeleteContractGroupMutation,
    useGetContractGroupByIdQuery,
    useGetContractUrlMutation,
    usePostContractGroupMutation,
    usePutContractGroupMutation,
    useReturnContractMutation,
} from "@/redux/contractApi";
import { useGetTermListQuery } from "@/redux/termApi";
import { useGetInsuranceConfigDetailQuery, useGetInsuranceConfigListQuery } from "@/redux/rentCorpApi";
import { InsuranceManagerInfo } from "./components/InsuranceManagerInfo";

// 초기 계약서 객체
const initialContract = {
    rentCorpId: null,
    carId: null,
    contractCar: {
        carNumber: "",
        cho: "",
        manufacturerName: "",
        modelName: "",
        modelDetailName: "",
        className: "",
        trimName: "",
        bmNo: "",
        boiNo: "",
        boNo: "",
        bsNo: "",
        bdNo: "",
        firstDate: "",
        color: "",
        fuel: "",
        motorType: "",
        displacement: 0,
        mileage: 0,
        gasCurrentAmount: 0,
    },
    insuranceItems: [
        {
            insuranceItemType: "DAEIN_PAY_1", // 대인배상1
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEIN_PAY_2", // 대인배상2
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEMUL_PAY", // 대물배상
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "NO_INS_INJURY", // 무보험차상해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_DEAD", // 자동차상해(사망)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_INJURY", // 자동차상해(부상)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_BLOCK", // 자동차상해(장해)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "SELF_CAR_DAMAGE", // 자기차량손해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
            selfCarDamageRangeType: null, // "PRICE" or "RATE" 선택값
            selfCarDamageRateType: null,
        },
        {
            insuranceItemType: "EMERGENCY_SERVICE", // 긴급출동
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
    ],
    contractDrivers: [
        {
            contractDriverType: "MAIN",
            name: "",
            idNumber: "",
            phone: "",
            address: "",
            addressDetail: "",
            driverLicenseType: "",
            driverLicenseNumber: "",
            serialNumber: "",
            issueDate: "",
        },
    ],
    additionalDriverStatus: false,
};

// 팝업 컴포넌트
const ChargeFaxPopup = ({ faxNumber, setFaxNumber, onConfirm, onClose }) =>
    ReactDOM.createPortal(
        <div className="popup-layer">
            <div className="layer active" onClick={onClose}></div>
            <div className="popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">보험사 팩스번호 입력</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="form-item">
                        <label htmlFor="faxNumber">보험사 팩스번호</label>
                        <input
                            type="text"
                            id="faxNumber"
                            value={faxNumber || ""}
                            onChange={(e) => setFaxNumber(e.target.value)}
                            placeholder="팩스번호를 입력하세요"
                        />
                    </div>
                </div>
                <div className="popup-bottom">
                    <button type="button" className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="button" className="btn btn-primary btn-confirm" onClick={onConfirm}>
                        확인
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );

export const ContractDetail = () => {
    const { contractGroupId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const passedCarId = location.state?.carId;
    const passedOrderId = location.state?.orderId;

    const { formData, handleChange, setFormData } = useFormInput({
        // 공통
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        servicePopupStatus: false,
        addressPopupStatus: false,
        addressPopupTarget: null, // "MAIN" | "ADDITIONAL" | "ALLOCATION_RELEASE" | "ALLOCATION_RETURN" | "BUSINESS"
        agentPartnersPopupStatus: false,
        salesPartnersPopupStatus: false,
        vehicleListPopupStatus: false,
        vehicleReturnPopupStatus: false,
        vehicleReturnContractIdx: null,
        billingSettingPopupStatus: false,
        termSelectPopupStatus: false,
        orderListPopupStatus: false,
        id: null, // contractGroupId
        businessStatus: false,
        contractType: "",
        memoForClient: "",
        memoForContract: "",
        memoForInsCorp: "",
        contractUploads: [],
        contractTerms: [],
        driverLicenseFileName: "",
        driverLicense2FileName: "",
        bizLicenseFileName: "",
        etcFileName: "",
        addFileName: "",
        addTermFileName: "",
        contracts: [{ ...initialContract }],
        faxFileUploadId: undefined,
        faxFileUrl: undefined,
    });
    const {
        mode,
        toastMsg,
        confirmStatus,
        addressPopupStatus,
        agentPartnersPopupStatus,
        salesPartnersPopupStatus,
        vehicleReturnPopupStatus,
        billingSettingPopupStatus,
        isLoading,
    } = formData;
    const [selectedContractIndex, setSelectedContractIndex] = useState(0); // 선택한 계약서 인덱스
    const [insuranceConfigPopupStatus, setInsuranceConfigPopupStatus] = useState(false);
    const [selectedInsuranceConfigId, setSelectedInsuranceConfigId] = useState(null);
    const [chargePopupStatus, setChargePopupStatus] = useState(false);
    const [faxNumber, setFaxNumber] = useState(formData.contractInsurance?.faxNumber || "");

    const [postContractGroup, { data: postData, isLoading: isPostLoading, error: isPostError }] = usePostContractGroupMutation();
    const [putContractGroup, { data: putData, isLoading: isPutLoading, error: isPutError }] = usePutContractGroupMutation();
    const [deleteContractGroup, { isLoading: isDeleteLoading }] = useDeleteContractGroupMutation();
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();
    const [doSearchCar, { data: searchCarData, isSearchLoading, isSearchError }] = useLazySearchCarByNumberQuery();
    const [getCarById, { isLoading: carLoading, error: carError }] = useLazyGetCarByIdQuery();
    const [postCharge] = usePostChargeMutation();
    const [postPreCharge] = usePostPreChargeMutation();
    const [getOrderDetail, { data: detailData, isLoading: detailIsLoading, error: detailError }] = useGetOrderDetailMutation();
    const [returnContract, { isLoading: isReturnLoading }] = useReturnContractMutation();
    const {
        data: contractGroupData,
        isLoading: isContractGroupLoading,
        isFetching: isContractGroupFetching,
        error: contractGroupError,
    } = useGetContractGroupByIdQuery(contractGroupId, {
        skip: !contractGroupId,
        refetchOnMountOrArgChange: true,
    });
    const [getContractUrl] = useGetContractUrlMutation();

    // useEffect(() => {
    //     console.log(formData);
    // }, [formData]);

    // 보험 설정 목록 조회
    const { data: insuranceConfigList } = useGetInsuranceConfigListQuery(formData.contracts[selectedContractIndex]?.rentCorpId, {
        skip: !formData.contracts[selectedContractIndex]?.rentCorpId,
    });

    // 보험 설정 상세 조회
    const { data: insuranceConfigDetail } = useGetInsuranceConfigDetailQuery(
        {
            rentCorpId: formData.contracts[selectedContractIndex]?.rentCorpId,
            insuranceConfigId: selectedInsuranceConfigId,
        },
        {
            skip: !formData.contracts[selectedContractIndex]?.rentCorpId || !selectedInsuranceConfigId,
        }
    );

    // 보험 설정 상세 조회 결과 처리
    useEffect(() => {
        if (insuranceConfigDetail?.data) {
            const processedInsuranceItems = insuranceConfigDetail.data.insuranceConfigItems.map((item) => ({
                id: item.id,
                insuranceItemType: item.insuranceItemType,
                joinYn: item.joinYn,
                limitType: item.limitType,
                selfCarDamageRangeType: item.selfCarDamageRangeType,
                selfCarDamageRateType: item.selfCarDamageRateType,
                compensationRange: item.compensationRange,
                selfPay: item.selfPay,
            }));

            setFormData((prev) => {
                const newContracts = [...prev.contracts];

                newContracts[selectedContractIndex] = {
                    ...newContracts[selectedContractIndex],
                    insuranceItems: processedInsuranceItems,
                };

                const result = {
                    ...prev,
                    contracts: newContracts,
                };
                return result;
            });
            setInsuranceConfigPopupStatus(false);
        }
    }, [insuranceConfigDetail, selectedContractIndex, setFormData]);

    const handleInsuranceConfigLoad = () => {
        setInsuranceConfigPopupStatus(true);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // 1) 모드 설정 (UPDATE / CREATE)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (contractGroupId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: contractGroupId,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                mode: "CREATE",
            }));
        }
    }, [contractGroupId, setFormData]);

    // ─────────────────────────────────────────────────────────────────────────
    // 2) UPDATE 모드일 때: 계약 상세조회 (contractDetailData) 매핑
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        // CREATE 모드일 때 early return
        if (!contractGroupId) return;

        if (contractGroupData && contractGroupData.data) {
            const response = contractGroupData.data;
            // contractDrivers 변환: idNumber 분리 및 driverLicenseNumber 처리
            const mappedContractDrivers = (response.contractDrivers || []).map((driver) => {
                let updatedDriver = { ...driver };
                // idNumber 분리: 앞 6자리와 뒷 7자리
                if (updatedDriver.idNumber && updatedDriver.idNumber.length >= 7) {
                    updatedDriver.idNumberF = updatedDriver.idNumber.slice(0, 6);
                    updatedDriver.idNumberB = updatedDriver.idNumber.slice(6); // 뒷 7자리 모두 처리
                }
                // driverLicenseNumber 분리: 앞 2자리를 Number 타입의 localCode로 저장하고, 나머지 문자열 재설정
                if (updatedDriver.driverLicenseNumber && updatedDriver.driverLicenseNumber.length >= 2) {
                    updatedDriver.localCode = Number(updatedDriver.driverLicenseNumber.slice(0, 2));
                    updatedDriver.driverLicenseNumber = updatedDriver.driverLicenseNumber.slice(2);
                }
                return updatedDriver;
            });
            // 제2 운전자가 있으면 additionalDriverStatus를 true로 설정
            const additionalDriverExists = mappedContractDrivers.some((driver) => driver.contractDriverType === "ADDITIONAL");
            // contractBusinessInfo가 존재하면 businessStatus를 true로 설정
            const businessStatus = response.contractBusinessInfo ? true : false;

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                ...response,
                // contractDrivers 변환 결과 반영
                contractDrivers: mappedContractDrivers,
                additionalDriverStatus: additionalDriverExists,
                businessStatus: businessStatus,
                faxFileUploadId: response.charge?.faxFileUploadId || undefined,
                faxFileUrl: response.charge?.faxFileUrl || undefined,
                // 필요시 contractCar 등 다른 필드도 추가 매핑
            }));
        }
    }, [contractGroupId, contractGroupData, setFormData]);

    // ─────────────────────────────────────────────────────────────────────────
    // 3) CREATE 모드일 때: passedCarId 또는 passedOrderId 처리
    // ─────────────────────────────────────────────────────────────────────────
    // 3-1) passedCarId가 있으면 차량 조회
    useEffect(() => {
        if (!contractGroupId && passedCarId) {
            // passedCarId가 존재하면 handleSearchCar() 로직 실행
            // (handleSearchCar 내부에서 doSearchCar 호출 → formData에 세팅)
            handleSearchCar(passedCarId);
        }
    }, [contractGroupId, passedCarId]);

    // 3-2) passedOrderId가 있으면 오더 상세조회 → formData에 세팅
    const loadOrderDetail = async (orderId) => {
        try {
            const res = await getOrderDetail(orderId).unwrap();
            const response = res.data;
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                orderId: orderId,
                platformCommissionRate: response?.platformCommissionRate,
                contractType: response?.orderType,
                accidentCar: {
                    ...response?.accidentCar,
                    bdNo: response?.bdNo,
                    bmNo: response?.bmNo,
                    boNo: response?.boNo,
                    boiNo: response?.boiNo,
                    bsNo: response?.bsNo,
                    carNumber: response?.carNumber,
                    cho: response?.cho,
                    className: response?.className,
                    displacement: comma(response?.displacement),
                    manufacturerName: response?.manufacturerName,
                    modelDetailName: response?.modelDetailName,
                    modelName: response?.modelName,
                    trimName: response?.trimName,
                },
                customerName: response?.orderCustomer?.name,
                customerPhone: response?.orderCustomer?.phone,
                contractInsurance: {
                    ...response?.contractInsurance,
                    id: response?.orderInsurance.id,
                    chargeMethod:
                        response?.orderInsurance?.chargeMethod === "RENT_PRICE"
                            ? response?.orderInsurance?.rentPriceTableId
                            : response?.orderInsurance?.chargeMethod,
                    chargeRate: response?.orderInsurance?.chargeRate,
                    insuranceCorpId: response?.orderInsurance?.insuranceCorpId,
                    insuranceCorpName: response?.orderInsurance.insuranceCorpName,
                    receiptNumber: response?.orderInsurance.receiptNumber,
                    prefixNumber: response?.orderInsurance.prefixNumber,
                    receiptAddNumber: response?.orderInsurance.receiptAddNumber,
                    managerName: response?.orderInsurance.managerName,
                    managerPhone: response?.orderInsurance.managerPhone,
                    faxNumber: response?.orderInsurance.faxNumber,
                    email: response?.orderInsurance.email,
                },
                contractMechanic: {
                    ...response?.contractMechanic,
                    mechanicCorpName: response?.orderMechanic?.mechanicCorpName,
                    phone: response?.orderMechanic?.phone,
                },
                contracts: prev.contracts.map((ct) => ({
                    ...ct,
                    contractCar: {
                        ...ct.contractCar,
                    },
                })),
                contractDrivers: prev.contractDrivers
                    ? prev.contractDrivers.map((driver) =>
                          driver.contractDriverType === "MAIN"
                              ? {
                                    ...driver,
                                    name: response?.orderCustomer?.name,
                                    phone: response?.orderCustomer?.phone,
                                }
                              : driver
                      )
                    : prev.contractDrivers,
            }));
        } catch (err) {
            console.error("오더 상세 조회 에러:", err);
        }
    };

    useEffect(() => {
        if (formData.mode === "CREATE") {
            if (!contractGroupId && passedOrderId) {
                // passedOrderId가 존재하면 getOrderDetail API 호출
                loadOrderDetail(passedOrderId);
            } else if (contractGroupId && formData.orderId) {
                loadOrderDetail(formData.orderId);
            }
        }
    }, [formData.orderId, contractGroupId, passedOrderId, getOrderDetail, setFormData, formData.mode]);

    const handleSelectCar = () => {
        setFormData((prev) => ({ ...prev, vehicleListPopupStatus: true }));
    };

    const handleSearchCar = async (carId, contractIndex = 0) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const res = await getCarById(carId).unwrap();
            const carData = res?.data;
            if (carData) {
                setFormData((prev) => {
                    const updatedContracts = [...prev.contracts];
                    updatedContracts[contractIndex] = {
                        ...updatedContracts[contractIndex],
                        contractCar: carData,
                        carId: carData.id,
                    };
                    return {
                        ...prev,
                        isLoading: false,
                        toastMsg: "차량 조회 완료",
                        contracts: updatedContracts,
                    };
                });
            } else {
                throw new Error("차량 조회 실패");
            }
        } catch (error) {
            console.error("차량 조회 에러:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 실패",
                isLoading: false,
            }));
        }
    };

    // 전달받은 차량번호가 있으면 자동으로 formData에 세팅하고 handleSearchCar 호출
    useEffect(() => {
        if (passedCarId) {
            setFormData((prev) => ({
                ...prev,
                contractCar: { ...prev.contractCar, id: passedCarId },
            }));
            handleSearchCar(passedCarId);
        }
    }, [passedCarId]);

    useEffect(() => {
        setFormData((prev) => {
            const updatedContracts = [...prev.contracts];
            const contract = updatedContracts[selectedContractIndex];

            if (prev.additionalDriverStatus) {
                // 추가 운전자가 없으면 추가
                if (!contract.contractDrivers.some((d) => d.contractDriverType === "ADDITIONAL")) {
                    contract.contractDrivers.push({
                        contractDriverType: "ADDITIONAL",
                        name: "",
                        idNumber: "",
                        phone: "",
                        address: "",
                        addressDetail: "",
                        bCode: "",
                        driverLicenseType: "",
                        driverLicenseNumber: "",
                        serialNumber: "",
                        issueDate: "",
                    });
                }
            }

            return {
                ...prev,
                contracts: updatedContracts,
            };
        });
    }, [formData.additionalDriverStatus, selectedContractIndex, setFormData]);

    // buildRequestBody – 그룹 레벨 필드와 contracts 배열 포함
    const buildRequestBody = () => {
        let reqBody = { ...formData };

        // UI용 상태 값들 제거
        delete reqBody.mode;
        delete reqBody.toastMsg;
        delete reqBody.confirmStatus;
        delete reqBody.servicePopupStatus;
        delete reqBody.id;
        delete reqBody.additionalDriverStatus;
        delete reqBody.businessStatus;
        delete reqBody.driverLicenseFileName;
        delete reqBody.driverLicense2FileName;
        delete reqBody.bizLicenseFileName;
        delete reqBody.etcFileName;
        delete reqBody.addFileName;
        delete reqBody.addTermFileName;
        delete reqBody.vehicleReturnPopupStatus;
        delete reqBody.billingSettingPopupStatus;
        delete reqBody.addressPopupStatus;
        delete reqBody.addressPopupTarget;
        delete reqBody.agentPartnersPopupStatus;
        delete reqBody.salesPartnersPopupStatus;
        delete reqBody.vehicleListPopupStatus;

        // accidentCar.displacement가 문자열로 콤마 포함 시 uncomma 처리
        if (reqBody.accidentCar && typeof reqBody.accidentCar.displacement === "string") {
            reqBody.accidentCar.displacement = Number(uncomma(reqBody.accidentCar.displacement));
        }

        // contracts 배열 내 각 계약서의 contractCar 필드 처리
        if (Array.isArray(reqBody.contracts)) {
            reqBody.contracts = reqBody.contracts.map((contract) => {
                if (contract?.contractCar) {
                    if (typeof contract?.contractCar.displacement === "string") {
                        contract.contractCar.displacement = Number(uncomma(contract?.contractCar.displacement));
                    }
                    if (typeof contract?.contractCar.mileage === "string") {
                        contract.contractCar.mileage = Number(uncomma(contract?.contractCar.mileage));
                    }
                    // 필요 시 다른 숫자 필드도 동일하게 처리
                }
                return contract;
            });
        }

        if (Array.isArray(reqBody.contractDrivers)) {
            reqBody.contractDrivers = reqBody.contractDrivers.map((driver) => {
                // 주민등록번호 idNumberF + idNumberB
                if (driver.idNumberF !== undefined && driver.idNumberB !== undefined) {
                    driver.idNumber = driver.idNumberF + driver.idNumberB;
                }
                // 면허번호 localCode + driverLicenseNumber 재설정
                if (driver.localCode && driver.driverLicenseNumber) {
                    driver.driverLicenseNumber = String(driver.localCode) + driver.driverLicenseNumber;
                }
                return driver;
            });
        }

        // 보험 관련 필드 처리
        if (Array.isArray(reqBody.insuranceItems)) {
            reqBody.insuranceItems = reqBody.insuranceItems.map((item) => ({
                ...item,
                imitType: item.imitType === "" ? null : item.imitType,
                compensationRange: item.compensationRange === "" ? null : item.compensationRange,
                selfPay: item.selfPay === "" ? null : item.selfPay,
                selfCarDamageRangeType: item.selfCarDamageRangeType === "" ? null : item.selfCarDamageRangeType,
                selfCarDamageRateType: item.selfCarDamageRateType === "" ? null : item.selfCarDamageRateType,
            }));
        }

        return reqBody;
    };

    // Validation 체크 함수
    const validateRegister = () => {
        // 필수 필드 검증 함수
        const validateField = (condition, message) => {
            if (condition) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: message,
                }));
                return false;
            }
            return true;
        };

        // 문자열 필드 검증
        const validateStringField = (value, message) => {
            return validateField(!value || value.trim() === "", message);
        };

        // 주민번호 검증
        const validateIdNumber = (idNumberF, idNumberB, prefix, isRequired = true) => {
            if (isRequired) {
                if (!validateStringField(idNumberF, `${prefix} 주민번호 앞 6자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberF.trim().length !== 6, `${prefix} 주민번호 앞자리는 6자리여야 합니다.`)) return false;
                if (!validateStringField(idNumberB, `${prefix} 주민번호 뒤 7자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberB.trim().length !== 7, `${prefix} 주민번호 뒷자리는 7자리여야 합니다.`)) return false;
            }
            return true;
        };

        // 주소 검증
        const validateAddress = (address, addressDetail, prefix) => {
            if (!validateStringField(address, `${prefix} 주소를 입력해주세요.`)) return false;
            if (!validateStringField(addressDetail, `${prefix} 상세주소를 입력해주세요.`)) return false;
            return true;
        };

        // 운전자 정보 검증
        // const validateDriver = (driver, prefix) => {
        //     if (!driver) return validateField(true, `${prefix} 정보를 입력해주세요.`);
        //     if (!validateStringField(driver.name, `${prefix}명을 입력해주세요.`)) return false;
        //     if (!validateStringField(driver.phone, `${prefix} 연락처를 입력해주세요.`)) return false;
        //     if (!validateIdNumber(driver.idNumberF, driver.idNumberB, prefix)) return false;
        //     if (!validateAddress(driver.address, driver.addressDetail, prefix)) return false;
        //     return true;
        // };

        // 3. 계약 유형 검증
        if (!validateStringField(formData.contractType, "계약 유형을 선택해주세요.")) return false;

        // 4. 계약자 정보 검증
        // const mainDriver = formData.contracts[selectedContractIndex]?.contractDrivers?.find(
        //     (driver) => driver.contractDriverType === "MAIN"
        // );
        // if (!validateDriver(mainDriver, "계약자")) return false;

        // 5. 사고차량 정보 검증
        if (!formData.accidentCar) return validateField(true, "사고차량 정보를 입력해주세요.");
        if (!validateStringField(formData.accidentCar.carNumber, "사고차량 번호를 입력해주세요.")) return false;
        if (!validateStringField(formData.accidentCar.bmNo, "사고차량 제조사를 입력해주세요.")) return false;
        if (!validateStringField(formData.accidentCar.boiNo, "사고차량 모델을 입력해주세요.")) return false;

        // 6. 계약 정보 및 계약차량 검증
        if (!formData.contracts || formData.contracts.length === 0) {
            return validateField(true, "계약 정보가 없습니다.");
        }

        for (const contract of formData.contracts) {
            if (!contract.contractCar) return validateField(true, "계약차량 정보가 누락되었습니다.");
            if (!validateStringField(contract.contractCar.carNumber, "계약차량 번호를 입력해주세요.")) return false;
            if (!validateStringField(contract.contractCar.manufacturerName, "계약차량 제조사를 입력해주세요.")) return false;
            if (!validateStringField(contract.contractCar.modelName, "계약차량 모델을 입력해주세요.")) return false;
        }

        // 7. 보험사 정보 검증
        if (!formData.contractInsurance) return validateField(true, "보험사 정보를 입력해주세요.");
        if (!validateStringField(formData.contractInsurance.insuranceCorpName, "보험사명을 입력해주세요.")) return false;
        // if (!validateStringField(formData.contractInsurance.receiptNumber, "접수번호를 입력해주세요.")) return false;

        // 제2운전자 정보 검증 (추가 운전자가 있는 경우)
        // if (formData.contracts[selectedContractIndex]?.additionalDriverStatus) {
        //     const additionalDriver = formData.contracts[selectedContractIndex]?.contractDrivers?.find(
        //         (driver) => driver.contractDriverType === "ADDITIONAL"
        //     );
        //     if (!validateDriver(additionalDriver, "제2운전자")) return false;
        // }

        // 12. 사업자 정보 검증 (사업자 정보가 있는 경우)
        if (formData.businessStatus) {
            if (!formData.contractBusinessInfo) return validateField(true, "사업자 정보를 입력해주세요.");
            if (!validateStringField(formData.contractBusinessInfo.corpName, "사업자 이름을 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.bizNumber, "사업자 번호를 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.managerName, "담당자 이름을 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.managerPhone, "담당자 연락처를 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.address, "사업자 주소를 입력해주세요.")) return false;

            const hasBizLic = formData.contractUploads?.some((item) => item.uploadType === "BIZ_LIC" && item.upload);
            if (!hasBizLic) return validateField(true, "사업자등록증 첨부파일을 첨부해주세요.");
        }

        return true;
    };

    const handleSubmit = async (action) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = buildRequestBody();
            await action(requestBody);

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/contract/status"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
                isLoading: false,
            }));
        } finally {
        }
    };

    // 차량 등록 시 호출 (CREATE)
    const handleSaveConfirm = async () => {
        await handleSubmit((requestBody) => postContractGroup(requestBody).unwrap());
    };

    // 차량 수정 시 호출 (UPDATE)
    const handleUpdateConfirm = async () => {
        await handleSubmit((requestBody) => putContractGroup({ contractGroupId: formData.id, body: requestBody }).unwrap());
    };

    const handleSave = () => {
        if (!validateRegister()) return;

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `계약서 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: mode === "CREATE" ? handleSaveConfirm : handleUpdateConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (postData) {
            navigate(`/contract/edit/${postData?.data.id}`);
        }
    }, [postData]);

    useEffect(() => {
        if (putData) {
            window.location.reload();
        }
    }, [putData]);

    const showConfirm = (title, description, buttons) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: title,
                confirmDescription: description,
                confirmButtons: buttons,
            },
        }));
    };

    // 삭제 확인 핸들러 (API 호출)
    const handleDeleteConfirm = async () => {
        try {
            await deleteContractGroup({
                contractGroupId: formData.id,
            }).unwrap();
            showConfirm("삭제가 완료되었습니다.", "", [
                {
                    label: "확인",
                    className: "btn-primary",
                    onClick: () => navigate("/contract/status"),
                },
            ]);
        } catch (error) {
            console.error("Failed to delete contract:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "계약서 정보 삭제에 실패했습니다.",
            }));
        }
    };

    // 삭제 버튼 클릭 시 확인 모달을 띄우고, "예" 버튼 클릭 시 handleDeleteConfirm 실행
    const handleDelete = () => {
        showConfirm("해당 계약서 정보를 삭제하시겠습니까?", "삭제 후에는 복구할 수 없습니다.", [
            { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
            {
                label: "아니오",
                className: "btn-cancel",
                onClick: () =>
                    setFormData((prev) => ({
                        ...prev,
                        confirmStatus: { confirmPopupStatus: false },
                    })),
            },
        ]);
    };

    // 공통 파일 업로드 핸들러
    const handleCommonFileChange = async (e, uploadType, idx) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;
            setFormData((prev) => {
                // 만약 uploadType이 "ADD_TERM"이면, contractTerms[idx] 업데이트
                if (uploadType === "ADD_TERM") {
                    const updatedTerms = [...(prev.contractTerms || [])];
                    updatedTerms[idx] = {
                        ...updatedTerms[idx],
                        upload: { id: newUploadId },
                        fileName: fileObj.name,
                    };
                    return { ...prev, contractTerms: updatedTerms };
                }
                // 그 외: contractUploads 배열 업데이트
                const updatedUploads = [...(prev.contractUploads || [])];
                const index = updatedUploads.findIndex((x) => x.uploadType === uploadType);
                if (index >= 0) {
                    updatedUploads[index] = {
                        ...updatedUploads[index],
                        upload: { id: newUploadId, fileName: fileObj.name },
                    };
                } else {
                    updatedUploads.push({ uploadType, upload: { id: newUploadId, fileName: fileObj.name } });
                }
                return { ...prev, contractUploads: updatedUploads };
            });
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "파일 업로드 실패",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const getTermFileName = (idx) => {
        return formData.contractTerms?.[idx]?.fileName || "";
    };

    const getFileName = (uploadType) => {
        const uploadObj = formData.contractUploads?.find((x) => x.uploadType === uploadType);
        return uploadObj?.upload?.fileName || "";
    };

    // 그룹 내 계약서 추가/삭제 핸들러
    const addContract = () => {
        setFormData((prev) => ({
            ...prev,
            contracts: [...prev.contracts, { ...initialContract }],
        }));
    };

    const deleteContract = (index) => {
        setFormData((prev) => ({
            ...prev,
            contracts: prev.contracts.filter((_, idx) => idx !== index),
        }));
    };

    // 약관 추가: mandatoryYn은 무조건 "Y"로 세팅, id는 임시 값(Date.now()) 사용 (실제 id는 서버에서 할당)
    const handleAddTerm = () => {
        setFormData((prev) => ({
            ...prev,
            contractTerms: [...(prev.contractTerms || []), { id: null, upload: { id: null }, name: "", mandatoryYn: "Y" }],
        }));
    };

    // 약관 선택 팝업
    const handleSelectTerms = () => {
        setFormData((prev) => ({ ...prev, termSelectPopupStatus: true }));
    };

    // Validation 체크 함수
    const validateCharge = () => {
        // 1. 청구방법(chargeMethod) 선택 여부 확인
        if (!formData.contractInsurance?.chargeMethod) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "청구방법을 선택해주세요.",
            }));
            return false;
        }
        // 2. 청구율(chargeRate) 선택 여부 확인
        if (!formData.contractInsurance?.chargeRate) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "청구율을 선택해주세요.",
            }));
            return false;
        }
        // 3. 모든 계약의 allocation?.releasedAt, allocation?.returnedAt 세팅 여부 확인
        if (!formData.contracts || formData.contracts.length === 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "계약 정보가 없습니다.",
            }));
            return false;
        }
        // if (formData.mode !== "CREATE") {
        //     if (!formData.contractInsurance?.faxNumber) {
        //         setFormData((prev) => ({
        //             ...prev,
        //             toastMsg: "보험사 팩스번호를 입력해주세요.",
        //         }));
        //         return false;
        //     }
        // }

        for (let contract of formData.contracts) {
            if (!contract.allocation?.releasedAt || !contract.allocation?.returnedAt) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "모든 계약의 대여일시와 반납일시를 설정해주세요.",
                }));
                return false;
            }
            // 4. 모든 계약에 billingInfo 객체가 존재하는지 확인
            if (!contract.billingInfo) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "모든 계약에 청구 정보가 설정되어 있어야 합니다.",
                }));
                return false;
            }
        }
        return true;
    };

    const handleShowFaxFile = (url) => {
        if (url) {
            window.open(url, "_blank");
        } else {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "생성된 파일이 존재하지 않습니다.",
            }));
        }
    };

    // 팝업 열기 함수
    const openChargePopup = () => {
        setFaxNumber(formData.contractInsurance?.faxNumber || "");
        setChargePopupStatus(true);
    };

    // 팝업 닫기 함수
    const closeChargePopup = () => {
        setChargePopupStatus(false);
    };

    // 팝업 내 확인 버튼 클릭 시
    const handleChargePopupConfirm = async (values) => {
        const { inputValues, radioValues, moduleStates } = values;
        const inputFax = inputValues.faxNumber;
        const chargeType = radioValues.chargeType;
        const selectedRentCorp = moduleStates.selectedRentCorp;

        // 유효성 검사
        if (!chargeType) {
            setFormData((prev) => ({ ...prev, toastMsg: "청구 타입을 선택해주세요." }));
            return false;
        }

        if (!inputFax) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험사 팩스번호를 입력해주세요." }));
            return false;
        }

        if (!selectedRentCorp) {
            setFormData((prev) => ({ ...prev, toastMsg: "청구 대상 사업자를 선택해주세요." }));
            return false;
        }

        // 기본 청구 유효성 검사
        if (!validateCharge()) {
            return false;
        }

        setFormData((prev) => ({
            ...prev,
            contractInsurance: { ...prev.contractInsurance, faxNumber: inputFax },
        }));

        console.log(selectedRentCorp.id);

        // 청구 타입에 따라 다른 API 호출
        if (chargeType === "normal") {
            await handleChargeWithFax(inputFax, selectedRentCorp);
        } else if (chargeType === "pre") {
            await handlePreChargeWithFax(inputFax, selectedRentCorp);
        }

        return true; // 성공 시 모달 닫기
    };

    // 팩스번호 받아서 선청구하기
    const handlePreChargeWithFax = async (fax, rentCorp) => {
        const chargeItems = formData.contracts.map((contract) => ({
            contractId: contract.id,
            rentPriceTableItemId: contract.billingInfo?.rentPrice?.id || null,
            baseRentPrice: Number(contract.billingInfo?.rentFee) || 0,
            rentDays: contract.billingInfo?.days || 0,
            rentHours: contract.billingInfo?.hours || 0,
            chargeRate: formData.contractInsurance?.chargeRate || 0,
            rentFee: Number(contract.billingInfo?.rentFee) || 0,
            insuranceChargeFee: Number(contract.billingInfo?.insuranceChargeFee) || 0,
            platformCommissionFee: Number(contract.billingInfo?.platformCommissionFee) || 0,
            exceptCommissionFee: Number(contract.billingInfo?.exceptCommissionFee) || 0,
            exceptAddTaxFee: Number(contract.billingInfo?.exceptAddTaxFee) || 0,
            orderCommissionFee: Number(contract.billingInfo?.orderCommissionFee) || 0,
            obtainEarningFee: Number(contract.billingInfo?.obtainEarningFee) || 0,
            allocation: {
                id: contract.allocation?.id || null,
                releasedAt: contract.allocation?.releasedAt || "",
                releaseMethod: contract.allocation?.releaseMethod || "",
                releaseAddress: contract.allocation?.releaseAddress || "",
                releaseAddressDetail: contract.allocation?.releaseAddressDetail || "",
                returnedAt: contract.allocation?.returnedAt || "",
                returnMethod: contract.allocation?.returnMethod || "",
                returnAddress: contract.allocation?.returnAddress || "",
                returnAddressDetail: contract.allocation?.returnAddressDetail || "",
                memo: contract.allocation?.memo || "",
            },
        }));

        const payload = {
            contractGroup: { id: formData.id },
            chargeItems,
            insuranceFaxNumber: fax,
            rentCorpId: rentCorp.id,
        };

        try {
            setFormData((prev) => ({ ...prev, isLoading: true }));
            await postPreCharge(payload).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "선청구 신청이 완료되었습니다.",
                isLoading: false,
            }));
            closeChargePopup();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "선청구하기 실패",
                isLoading: false,
            }));
        }
    };

    // 팩스번호를 받아서 청구하기
    const handleChargeWithFax = async (fax, rentCorp) => {
        const chargeItems = formData.contracts.map((contract) => ({
            contractId: contract.id,
            rentPriceTableItemId: contract.billingInfo?.rentPrice?.id || null,
            baseRentPrice: Number(contract.billingInfo?.rentFee) || 0,
            rentDays: contract.billingInfo?.days || 0,
            rentHours: contract.billingInfo?.hours || 0,
            chargeRate: formData.contractInsurance?.chargeRate || 0,
            rentFee: Number(contract.billingInfo?.rentFee) || 0,
            insuranceChargeFee: Number(contract.billingInfo?.insuranceChargeFee) || 0,
            platformCommissionFee: Number(contract.billingInfo?.platformCommissionFee) || 0,
            exceptCommissionFee: Number(contract.billingInfo?.exceptCommissionFee) || 0,
            exceptAddTaxFee: Number(contract.billingInfo?.exceptAddTaxFee) || 0,
            orderCommissionFee: Number(contract.billingInfo?.orderCommissionFee) || 0,
            obtainEarningFee: Number(contract.billingInfo?.obtainEarningFee) || 0,
            allocation: {
                id: contract.allocation?.id || null,
                releasedAt: contract.allocation?.releasedAt || "",
                releaseMethod: contract.allocation?.releaseMethod || "",
                releaseAddress: contract.allocation?.releaseAddress || "",
                releaseAddressDetail: contract.allocation?.releaseAddressDetail || "",
                returnedAt: contract.allocation?.returnedAt || "",
                returnMethod: contract.allocation?.returnMethod || "",
                returnAddress: contract.allocation?.returnAddress || "",
                returnAddressDetail: contract.allocation?.returnAddressDetail || "",
                memo: contract.allocation?.memo || "",
            },
        }));

        const payload = {
            contractGroup: { id: formData.id },
            chargeItems,
            insuranceFaxNumber: fax,
            rentCorpId: rentCorp.id,
        };

        try {
            setFormData((prev) => ({ ...prev, isLoading: true }));
            const res = await postCharge(payload).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "청구신청이 완료되었습니다.",
                isLoading: false,
                faxFileUrl: res.data.faxFileUrl || undefined,
                faxFileUploadId: res.data.faxFileUploadId || undefined,
            }));
            closeChargePopup();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "청구하기 실패",
                isLoading: false,
            }));
        }
    };

    const actionReturn = async (contractId, payload) => {
        try {
            await returnContract({
                contractGroupId: formData.id,
                contractId: contractId,
                body: payload,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                toastMsg: "반납 등록이 완료되었습니다.",
                confirmStatus: { confirmPopupStatus: false },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "반납 등록에 실패하였습니다.",
            }));
        }
    };

    // 약관 목록 조회
    const { data: termListData } = useGetTermListQuery();
    // 약관 데이터 처리
    useEffect(() => {
        if (termListData?.data && mode === "CREATE") {
            const activeTerms = termListData.data.filter((term) => term.exposedYn === "Y");
            setFormData((prev) => ({
                ...prev,
                contractTerms: activeTerms.map((term) => ({
                    termId: term.id,
                    name: term.name,
                    content: term.content,
                    checked: true,
                })),
            }));
        }
    }, [termListData, mode]);

    const handleContractUrlClick = async (contractId) => {
        try {
            const { data } = await getContractUrl({
                contractGroupId: formData.id,
                contractId: contractId,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "전자계약 URL",
                    confirmDescription: data || "",
                    confirmButtons: [
                        {
                            label: "URL 복사",
                            className: "btn-primary",
                            onClick: async () => {
                                if (data) {
                                    await navigator.clipboard.writeText(data);
                                    setFormData((prev) => ({
                                        ...prev,
                                        toastMsg: "URL이 클립보드에 복사되었습니다.",
                                        confirmStatus: { confirmPopupStatus: false },
                                    }));
                                }
                            },
                        },
                        {
                            label: "취소",
                            className: "btn-cancel",
                            onClick: () =>
                                setFormData((prev) => ({
                                    ...prev,
                                    confirmStatus: { confirmPopupStatus: false },
                                })),
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "URL 복사에 실패했습니다.",
            }));
        }
    };

    const handleReturnFinish = (result) => {
        if (result) {
            window.location.reload();
        }
    };

    const handleSaveInsuranceManager = () => {};

    // 차량 이미지 업로드 이벤트 핸들러
    const handleImageUploadComplete = ({ uploadId, uploadType, contractId, index, imageType }) => {
        setFormData((prev) => {
            const newCarImages = prev.carImages ? [...prev.carImages] : [];
            newCarImages[index] = {
                upload: { id: uploadId },
                carImageType: imageType,
            };
            return {
                ...prev,
                carImages: newCarImages,
            };
        });
    };

    // 차량 이미지 삭제 이벤트 핸들러
    const handleRemoveCarImage = (contractId, imageIndex) => {
        setFormData((prev) => {
            const newCarImages = prev.carImages ? [...prev.carImages] : [];
            newCarImages.splice(imageIndex, 1);
            return {
                ...prev,
                carImages: newCarImages,
            };
        });
    };

    // 차량 이미지 순서 변경 이벤트 핸들러
    const handleReorderCarImages = (contractId, reorderedImages) => {
        setFormData((prev) => ({
            ...prev,
            carImages: reorderedImages,
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">
                                {mode === "CREATE" ? "임대차 계약서 등록" : "임대차 계약서 상세"}{" "}
                                {formData.orderId && <span className="call-contract">CALL</span>}
                            </span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options"></div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        계약 메모 <i>계약서 기록 및 상태를 렌터카 업체와 공유할 수 있습니다.</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ContractMemo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            contractGroupId={formData.id}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 유형</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ContractInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            {formData.businessStatus && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">사업자 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <BusinessInfo
                                                formData={formData}
                                                handleChange={handleChange}
                                                setFormData={setFormData}
                                                getFileName={getFileName}
                                                handleCommonFileChange={handleCommonFileChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기본 정보</span>
                                    <div className="form-item_etc">
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline"
                                            onClick={() => navigate(`/contract/edit/basic-info/${contractGroupId}`)}
                                        >
                                            수정하기
                                        </button>
                                    </div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BasicInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사고차량 이미지</span>
                                </div>

                                <ImageUpload
                                    maxImages={50}
                                    labelText="이미지"
                                    inputIdPrefix="car_images"
                                    placeholderText="사진 업로드"
                                    onUploadComplete={handleImageUploadComplete}
                                    uploadType="carImages"
                                    contractIndex={selectedContractIndex}
                                    initialImages={formData.carImages?.filter((item) => item.carImageType === "ACCIDENT_CAR") || []}
                                    onRemoveImage={(imageIndex) => handleRemoveCarImage(selectedContractIndex, imageIndex)}
                                    onReorderImages={(reorderedImages) => handleReorderCarImages(selectedContractIndex, reorderedImages)}
                                    imageType="ACCIDENT_CAR"
                                    readOnly={true}
                                />
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험사 담당자 정보</span>
                                    <div className="form-item_etc">
                                        <button type="button" className="btn btn-xs btn-outline" onClick={handleSave}>
                                            저장
                                        </button>
                                    </div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceManagerInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기타 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OtherInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        계약서 특약사항 <i>고객 계약서에 기재됩니다.</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OtherEtcInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 그룹(계약서 그룹) 영역 – 계약 리스트 */}
                            <div className="form-group">
                                <div className="group-head">
                                    <div className="head-tit">계약 리스트</div>
                                    <div className="head-option"></div>
                                </div>

                                <div className="group-content">
                                    {formData.contracts.map((contract, idx) => (
                                        <div key={idx} className="group-item">
                                            <div className="group-item-head">
                                                <div className="head-tit">계약-{idx + 1}</div>
                                                <div className="head-option">
                                                    {mode !== "CREATE" && (
                                                        <>
                                                            {/* {idx >= 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-cancel"
                                                                    onClick={() => deleteContract(idx)}
                                                                >
                                                                    계약 지우기
                                                                </button>
                                                            )} */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-fourth"
                                                                onClick={() => {
                                                                    const accessToken = localStorage.getItem("access_token");
                                                                    window.open(
                                                                        `${process.env.REACT_APP_SERVER_URL}/contract_group/${contractGroupId}/contract/${contract?.id}?btn_expose_yn=Y&token=${accessToken}`,
                                                                        "_blank"
                                                                    );
                                                                }}
                                                            >
                                                                계약서 미리보기
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-fourth"
                                                                onClick={() => handleContractUrlClick(contract.id)}
                                                            >
                                                                전자계약 URL
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline"
                                                                onClick={() => {
                                                                    navigate(`/contract/edit/detail/${contractGroupId}`, {
                                                                        state: {
                                                                            contractIndex: idx,
                                                                            contractId: contract.id,
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                계약 상세보기
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="group-item-body">
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">계약 정보</span>
                                                        <div className="form-item_etc"></div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <ContractDetailInfo
                                                                formData={formData}
                                                                setFormData={setFormData}
                                                                contract={contract}
                                                                onContractChange={(newValues) => {
                                                                    setFormData((prev) => {
                                                                        const updated = [...prev.contracts];
                                                                        updated[idx] = {
                                                                            ...updated[idx],
                                                                            ...newValues,
                                                                        };
                                                                        return { ...prev, contracts: updated };
                                                                    });
                                                                }}
                                                                handleSearchCar={() => {
                                                                    setSelectedContractIndex(idx);
                                                                    handleSelectCar();
                                                                }}
                                                                allocation={contract?.allocation}
                                                                onAllocationChange={(newValues) =>
                                                                    setFormData((prev) => {
                                                                        const updated = [...prev.contracts];
                                                                        updated[idx] = {
                                                                            ...updated[idx],
                                                                            allocation: { ...updated[idx].allocation, ...newValues },
                                                                        };
                                                                        return { ...prev, contracts: updated };
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                        {mode !== "CREATE" && (
                                                            <div className="item-list">
                                                                <BillingInfo
                                                                    formData={formData}
                                                                    setFormData={setFormData}
                                                                    contract={contract}
                                                                    onContractChange={(newValues) => {
                                                                        setFormData((prev) => {
                                                                            const updated = [...prev.contracts];
                                                                            updated[idx] = {
                                                                                ...updated[idx],
                                                                                billingInfo: { ...updated[idx].billingInfo, ...newValues },
                                                                            };
                                                                            return { ...prev, contracts: updated };
                                                                        });
                                                                    }}
                                                                    selectedContractIndex={idx}
                                                                    setSelectedContractIndex={setSelectedContractIndex}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 관련 서류</span>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <AttachedFile
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            getFileName={getFileName}
                                            handleCommonFileChange={handleCommonFileChange}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            {(mode !== "CREATE" || formData.orderId) && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">입금 내역</span>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <DepositHistory formData={formData} setFormData={setFormData} contractGroupId={formData.id} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(mode !== "CREATE" || formData.orderId) && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">종합 청구 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <TotalBillingInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-button">
                        <div className="button-row">
                            <button type="button" className="btn btn-xs btn-outline" onClick={() => navigate(-1)}>
                                계약 목록
                            </button>
                            <button type="button" className="btn btn-xs btn-fourth" onClick={() => openChargePopup()}>
                                청구 신청
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        vehicleReturnPopupStatus: true,
                                    }));
                                }}
                            >
                                반납 등록
                            </button>
                            {mode !== "CREATE" && (
                                <>
                                    <button type="button" className="btn btn-xs btn-cancel" onClick={handleDelete}>
                                        계약서 삭제
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-primary"
                                        onClick={() => navigate(`/contract/register/contract/${contractGroupId}`)}
                                    >
                                        계약 추가
                                    </button>
                                </>
                            )}
                            <button type="button" className="btn btn-sm btn-primary" onClick={handleSave}>
                                계약서 저장
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => {
                            const [driverType, contractIndex] = prev.addressPopupTarget.split("_");
                            const updatedContracts = [...prev.contracts];
                            const contract = updatedContracts[contractIndex];

                            // 운전자 정보 업데이트
                            const updatedDrivers = contract.contractDrivers.map((driver) => {
                                if (driver.contractDriverType === driverType) {
                                    return {
                                        ...driver,
                                        address: data.address,
                                        addressDetail: data.addressDetail,
                                        bCode: data.bCode,
                                    };
                                }
                                return driver;
                            });

                            updatedContracts[contractIndex] = {
                                ...contract,
                                contractDrivers: updatedDrivers,
                            };

                            return {
                                ...prev,
                                contracts: updatedContracts,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                        })
                    }
                />
            )}

            {agentPartnersPopupStatus && (
                <AgentPartnersPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, agentPartnersPopupStatus: false }))}
                    formData={formData.productItemType}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onAgentPartnerSelect={(partnerData) => {
                        // setFormData((prev) => {
                        //     const updated = [...prev.contracts];
                        //     updated[selectedContractIndex] = {
                        //         ...updated[selectedContractIndex],
                        //         contractCar: carData,
                        //         carId: carData.id,
                        //     };
                        //     return { ...prev, contracts: updated };
                        // });
                    }}
                />
            )}

            {salesPartnersPopupStatus && (
                <SalesPartnersPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, salesPartnersPopupStatus: false }))}
                    formData={formData.productItemType}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onSalesPartnerSelect={(partnerData) => {
                        // setFormData((prev) => {
                        //     const updated = [...prev.contracts];
                        //     updated[selectedContractIndex] = {
                        //         ...updated[selectedContractIndex],
                        //         contractCar: carData,
                        //         carId: carData.id,
                        //     };
                        //     return { ...prev, contracts: updated };
                        // });
                    }}
                />
            )}

            {formData.vehicleListPopupStatus && (
                <VehicleListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleListPopupStatus: false }))}
                    setFormData={setFormData}
                    // 전달하는 rentCorpId는 해당 계약서의 rentCorpId
                    rentCorpId={formData.contracts[selectedContractIndex].rentCorpId}
                    onRentCorpSelect={(value) => {
                        setFormData((prev) => {
                            const updated = [...prev.contracts];
                            updated[selectedContractIndex] = {
                                ...updated[selectedContractIndex],
                                rentCorpId: value,
                            };
                            return { ...prev, contracts: updated };
                        });
                    }}
                    onVehicleSelect={(vehicleData) => {
                        getCarById(vehicleData.id)
                            .then((res) => {
                                const carData = res.data?.data;
                                setFormData((prev) => {
                                    const updated = [...prev.contracts];
                                    updated[selectedContractIndex] = {
                                        ...updated[selectedContractIndex],
                                        contractCar: carData,
                                        carId: carData.id,
                                        insuranceItems: carData?.insurance?.insuranceItems,
                                    };
                                    return { ...prev, contracts: updated };
                                });
                            })
                            .catch((error) => {
                                console.error("차량 조회 에러:", error);
                            });
                    }}
                />
            )}

            {formData.termSelectPopupStatus && (
                <TermListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, termSelectPopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onTermsSelect={(selectedTermsData) => {
                        setFormData((prev) => ({
                            ...prev,
                            contractTerms: selectedTermsData,
                        }));
                    }}
                />
            )}

            {formData.orderListPopupStatus && (
                <OrderListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, orderListPopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onOrderSelect={(order) => {
                        loadOrderDetail(order.id);
                    }}
                />
            )}

            {vehicleReturnPopupStatus && (
                <VehicleReturnPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleReturnPopupStatus: false, vehicleReturnContractIdx: null }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    resultCallback={handleReturnFinish}
                />
            )}

            {billingSettingPopupStatus && (
                <BillingSettingPopup
                    layer={true}
                    onClose={() => {
                        setFormData((prev) => ({
                            ...prev,
                            billingSettingPopupStatus: false,
                        }));
                        setSelectedContractIndex(null);
                    }}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    selectedContractIndex={selectedContractIndex}
                />
            )}
            {insuranceConfigPopupStatus && (
                <InsuranceConfigPopup
                    layer={true}
                    onClose={() => setInsuranceConfigPopupStatus(false)}
                    formData={formData}
                    setFormData={setFormData}
                    onInsuranceConfigSelect={(config) => {
                        setSelectedInsuranceConfigId(config.id);
                        setFormData((prev) => {
                            const newContracts = [...prev.contracts];
                            newContracts[selectedContractIndex] = {
                                ...newContracts[selectedContractIndex],
                                rentCorpId: config.rentCorpId,
                            };
                            return {
                                ...prev,
                                contracts: newContracts,
                            };
                        });
                    }}
                />
            )}
            {chargePopupStatus && (
                <InputPopup
                    title="청구 신청"
                    description="청구 타입을 선택하고 보험사 팩스번호를 입력한 후, 청구 대상 사업자를 선택해주세요."
                    width="720px"
                    radios={[
                        {
                            name: "chargeType",
                            label: "청구 타입",
                            options: [
                                { value: "normal", label: "청구" },
                                { value: "pre", label: "선청구" },
                            ],
                            value: "",
                        },
                    ]}
                    inputs={[
                        {
                            name: "faxNumber",
                            label: "보험사 팩스번호",
                            type: "text",
                            placeholder: "팩스번호를 입력하세요",
                            value: faxNumber,
                        },
                    ]}
                    modules={[
                        {
                            type: "rentCorpSelection",
                            props: {},
                        },
                    ]}
                    buttons={[
                        {
                            label: "취소",
                            className: "btn-cancel",
                            onClick: closeChargePopup,
                        },
                        {
                            label: "확인",
                            className: "btn-primary",
                            closeOnClick: false,
                            onClick: handleChargePopupConfirm,
                        },
                    ]}
                    onClose={closeChargePopup}
                />
            )}
        </>
    );
};
